/**
 * Presentation Layer - Main Navigator (Web)
 * 
 * Navegación optimizada para web con sidebar permanente en desktop.
 */

import React, {useState, useEffect} from 'react';
import {createStackNavigator} from '@react-navigation/stack';
import {useNavigation, useNavigationState} from '@react-navigation/native';
import {View, StyleSheet, Text, TouchableOpacity, useWindowDimensions} from 'react-native';
import {useAuth} from '../hooks/useAuth';
import {DashboardScreen} from '../screens/DashboardScreen';
import {ScanScreen} from '../screens/ScanScreen';
import {ServiceSelectionScreen} from '../screens/ServiceSelectionScreen';
import {DeliverySuccessScreen} from '../screens/DeliverySuccessScreen';
import {DeliveryErrorScreen} from '../screens/DeliveryErrorScreen';
import {ScanErrorScreen} from '../screens/ScanErrorScreen';
import {Beneficiary} from '../../domain/scan/entities/Beneficiary';
import {Icon} from '../../shared/components/Icon';
import {MobileDrawer} from '../components/MobileDrawer';

export type MainStackParamList = {
  Dashboard: undefined;
  Scan: undefined;
  ScanError: {errorMessage: string};
  ServiceSelection: {beneficiary: Beneficiary; qrCode: string};
  DeliverySuccess: undefined;
  DeliveryError: {errorMessage: string};
};

const Stack = createStackNavigator<MainStackParamList>();

const Sidebar: React.FC<{onClose?: () => void}> = ({onClose}) => {
  const navigation = useNavigation<any>();
  const {user, logout} = useAuth();
  const dimensions = useWindowDimensions();
  const isMobile = dimensions.width < 768;
  
  const currentRoute = useNavigationState(state => {
    const route = state?.routes[state.index];
    return route?.name || 'Dashboard';
  });

  const menuItems = [
    {label: 'Dashboard', icon: 'apps', route: 'Dashboard'},
    {label: 'Escanear QR', icon: 'qr-code-2', route: 'Scan'},
  ];

  const getInitial = () => {
    const name = user?.name || 'Usuario';
    return name.charAt(0).toUpperCase();
  };

  const handleNavigate = (route: string) => {
    navigation.navigate(route);
    onClose?.();
  };

  const handleLogout = () => {
    logout();
    onClose?.();
  };

  return (
    <View style={styles.sidebar}>
      {/* Header con botón de cerrar en móvil */}
      <View style={styles.sidebarHeader}>
        {isMobile && onClose && (
          <TouchableOpacity onPress={onClose} style={styles.mobileCloseButton}>
            <Icon name="close" size={24} color="#fff" />
          </TouchableOpacity>
        )}
        <View style={styles.avatarContainer}>
          <Text style={styles.avatarText}>{getInitial()}</Text>
        </View>
        <Text style={styles.userName} numberOfLines={1}>{user?.name || 'Provider User'}</Text>
        <Text style={styles.userEmail} numberOfLines={1}>{user?.email || 'User'}</Text>
      </View>

      {/* Menu Items */}
      <View style={styles.menuContainer}>
        {menuItems.map((item, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.menuItem,
              currentRoute === item.route && styles.menuItemActive,
            ]}
            onPress={() => handleNavigate(item.route)}>
            <Icon
              name={item.icon}
              size={24}
              color={currentRoute === item.route ? '#fff' : '#93c5fd'}
            />
            <Text
              style={[
                styles.menuItemText,
                currentRoute === item.route && styles.menuItemTextActive,
              ]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Logout */}
      <View style={styles.sidebarFooter}>
        <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
          <Icon name="logout" size={24} color="#ef4444" />
          <Text style={styles.logoutText}>Cerrar Sesión</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const MainContent: React.FC = () => {
  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        animationEnabled: false,
      }}>
      <Stack.Screen name="Dashboard" component={DashboardScreen} />
      <Stack.Screen name="Scan">
        {(props) => (
          <ScanScreen
            {...props}
            onScanSuccess={(beneficiary: Beneficiary, qrCode: string) => {
              props.navigation.navigate('ServiceSelection', {beneficiary, qrCode});
            }}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="ScanError" component={ScanErrorScreen} />
      <Stack.Screen name="ServiceSelection" component={ServiceSelectionScreen} />
      <Stack.Screen name="DeliverySuccess" component={DeliverySuccessScreen} />
      <Stack.Screen name="DeliveryError" component={DeliveryErrorScreen} />
    </Stack.Navigator>
  );
};

export const MainNavigator: React.FC = () => {
  const dimensions = useWindowDimensions();
  const isDesktop = dimensions.width >= 768;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Rutas donde NO queremos mostrar el sidebar (flujo de escaneo)
  const currentRoute = useNavigationState(state => {
    const route = state?.routes[state.index];
    return route?.name || 'Dashboard';
  });
  
  const hideSidebarRoutes = ['Scan', 'ServiceSelection', 'ScanError', 'DeliverySuccess', 'DeliveryError'];
  const shouldShowSidebar = isDesktop && !hideSidebarRoutes.includes(currentRoute);
  const shouldShowSidebarPeek = !isDesktop && !hideSidebarRoutes.includes(currentRoute);

  return (
    <View style={styles.container}>
      {shouldShowSidebar && <Sidebar />}
      
      {/* Peek del sidebar para móvil - franja vertical en el borde izquierdo */}
      {shouldShowSidebarPeek && (
        <TouchableOpacity
          style={styles.sidebarPeek}
          onPress={() => setIsMobileMenuOpen(true)}
          activeOpacity={0.8}>
          <Icon name="menu" size={24} color="#fff" />
        </TouchableOpacity>
      )}

      {/* Drawer del menú para móvil */}
      <MobileDrawer
        isOpen={isMobileMenuOpen && !isDesktop}
        onClose={() => setIsMobileMenuOpen(false)}>
        <Sidebar onClose={() => setIsMobileMenuOpen(false)} />
      </MobileDrawer>

      <View style={styles.content}>
        <MainContent />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  sidebar: {
    width: 280,
    backgroundColor: '#1e3a8a',
    borderRightWidth: 1,
    borderRightColor: '#3b82f6',
  },
  sidebarHeader: {
    padding: 20,
    paddingTop: 40,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#3b82f6',
    position: 'relative',
  },
  mobileCloseButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    zIndex: 10,
  },
  avatarContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#3b82f6',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#60a5fa',
  },
  avatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
    color: '#fff',
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  userEmail: {
    fontSize: 12,
    color: '#93c5fd',
    marginTop: 4,
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  menuContainer: {
    flex: 1,
    paddingTop: 20,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 8,
    marginVertical: 4,
    borderRadius: 8,
  },
  menuItemActive: {
    backgroundColor: '#3b82f6',
  },
  menuItemText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#93c5fd',
  },
  menuItemTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  sidebarFooter: {
    borderTopWidth: 1,
    borderTopColor: '#3b82f6',
    padding: 8,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 8,
    borderRadius: 8,
  },
  logoutText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#ef4444',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  sidebarPeek: {
    position: 'absolute',
    top: 16,
    left: 0,
    width: 56,
    height: 56,
    backgroundColor: '#1e3a8a',
    zIndex: 999,
    justifyContent: 'center',
    alignItems: 'center',
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    shadowColor: '#000',
    shadowOffset: {width: 2, height: 0},
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 8,
  },
});
